import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import AppLayout from '../components/AppLayout';
import Modal from '../components/Modal';
import { useApi } from '../hooks/useApi';
const severityLabel = {
    maintenance: 'Maintenance',
    incident: 'Incident',
    critical: 'Critical'
};
const statusLabel = {
    open: 'Open',
    investigating: 'Investigating',
    fixing: 'Fixing',
    monitoring: 'Monitoring',
    closed: 'Closed'
};
const getNextStatuses = (status, severity) => {
    if (severity === 'maintenance') {
        if (status === 'open') {
            return ['closed'];
        }
        return [];
    }
    switch (status) {
        case 'open':
            return ['investigating'];
        case 'investigating':
            return ['fixing'];
        case 'fixing':
            return ['monitoring', 'closed'];
        case 'monitoring':
            return ['closed'];
        default:
            return [];
    }
};
const toRecord = (incident) => ({
    id: incident.id,
    name: incident.name,
    status: incident.status,
    severity: incident.severity,
    createdAt: incident.createdAt,
    updatedAt: incident.updatedAt,
    messageCount: incident.messages.length
});
const IncidentsPage = () => {
    const { get, post, del } = useApi();
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createData, setCreateData] = useState({
        name: '',
        description: '',
        severity: 'incident'
    });
    const [createError, setCreateError] = useState(null);
    const [statusModalIncident, setStatusModalIncident] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [statusError, setStatusError] = useState(null);
    const [messageModalIncident, setMessageModalIncident] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [messageError, setMessageError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const loadIncidents = async () => {
        setLoading(true);
        try {
            const data = await get('/incidents');
            setIncidents([...data].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
        }
        finally {
            setLoading(false);
        }
    };
    const handleDeleteIncident = async () => {
        if (!deleteTarget) {
            return;
        }
        await del(`/incidents/${deleteTarget.id}`);
        setIncidents((prev) => prev.filter((item) => item.id !== deleteTarget.id));
        setDeleteTarget(null);
    };
    useEffect(() => {
        loadIncidents();
    }, []);
    const updateIncidentList = (detail) => {
        const record = toRecord(detail);
        setIncidents((prev) => {
            const exists = prev.some((item) => item.id === record.id);
            const next = exists
                ? prev.map((item) => (item.id === record.id ? record : item))
                : [record, ...prev];
            return next.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        });
    };
    const availableStatusOptions = statusModalIncident
        ? getNextStatuses(statusModalIncident.status, statusModalIncident.severity)
        : [];
    const handleCreateIncident = async (event) => {
        event.preventDefault();
        setCreateError(null);
        try {
            const payload = {
                name: createData.name.trim(),
                description: createData.description.trim(),
                severity: createData.severity
            };
            const incident = await post('/incidents', payload);
            updateIncidentList(incident);
            setCreateModalOpen(false);
            setCreateData({ name: '', description: '', severity: 'incident' });
        }
        catch (error) {
            setCreateError(error.message);
        }
    };
    const handleStatusSubmit = async (event) => {
        event.preventDefault();
        if (!statusModalIncident || !newStatus) {
            return;
        }
        setStatusError(null);
        try {
            const updated = await post(`/incidents/${statusModalIncident.id}/status`, { status: newStatus });
            updateIncidentList(updated);
            setStatusModalIncident(null);
            setNewStatus('');
        }
        catch (error) {
            setStatusError(error.message);
        }
    };
    const handleMessageSubmit = async (event) => {
        event.preventDefault();
        if (!messageModalIncident) {
            return;
        }
        setMessageError(null);
        try {
            const payload = {
                message: messageText.trim()
            };
            if (!payload.message) {
                setMessageError('Message is required');
                return;
            }
            const updated = await post(`/incidents/${messageModalIncident.id}/messages`, payload);
            updateIncidentList(updated);
            setMessageModalIncident(null);
            setMessageText('');
        }
        catch (error) {
            setMessageError(error.message);
        }
    };
    return (_jsxs(AppLayout, { title: "Incidents", actions: _jsx("button", { onClick: () => {
                setCreateModalOpen(true);
                setCreateError(null);
            }, disabled: loading, children: "New incident" }), children: [_jsxs("div", { className: "card", children: [_jsx("h3", { style: { marginTop: 0 }, children: "Incident log" }), _jsxs("table", { className: "table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Name" }), _jsx("th", { children: "Severity" }), _jsx("th", { children: "Status" }), _jsx("th", { children: "Messages" }), _jsx("th", { children: "Created" }), _jsx("th", { children: "Updated" }), _jsx("th", { style: { textAlign: 'right' }, children: "Actions" })] }) }), _jsxs("tbody", { children: [incidents.map((incident) => (_jsxs("tr", { children: [_jsx("td", { children: incident.name }), _jsx("td", { children: _jsx("span", { className: `severity-chip severity-chip--${incident.severity}`, children: severityLabel[incident.severity] }) }), _jsx("td", { children: _jsx("span", { className: `incident-status-chip incident-status-chip--${incident.status}`, children: statusLabel[incident.status] }) }), _jsx("td", { children: incident.messageCount }), _jsx("td", { children: new Date(incident.createdAt).toLocaleString() }), _jsx("td", { children: new Date(incident.updatedAt).toLocaleString() }), _jsxs("td", { style: { textAlign: 'right' }, children: [_jsx("button", { type: "button", className: "icon-button", title: "Change status", onClick: () => {
                                                            const options = getNextStatuses(incident.status, incident.severity);
                                                            setStatusModalIncident(incident);
                                                            setStatusError(null);
                                                            setNewStatus(options.length > 0 ? options[0] : '');
                                                        }, disabled: incident.status === 'closed', children: "\uD83D\uDD01" }), _jsx("button", { type: "button", className: "icon-button", title: "Add message", onClick: () => {
                                                            setMessageModalIncident(incident);
                                                            setMessageText('');
                                                            setMessageError(null);
                                                        }, disabled: incident.status === 'closed', children: "\u2795" }), _jsx("button", { type: "button", className: "icon-button icon-button--danger", title: "Remove incident", onClick: () => setDeleteTarget(incident), children: "\uD83D\uDDD1\uFE0F" })] })] }, incident.id))), !incidents.length && !loading && (_jsx("tr", { children: _jsx("td", { colSpan: 7, style: { textAlign: 'center', padding: '1rem' }, children: "No incidents recorded." }) })), loading && (_jsx("tr", { children: _jsx("td", { colSpan: 7, style: { textAlign: 'center', padding: '1rem' }, children: "Loading incidents\u2026" }) }))] })] })] }), _jsx(Modal, { open: createModalOpen, title: "New incident", onClose: () => {
                    setCreateModalOpen(false);
                    setCreateError(null);
                }, width: "min(520px, 95vw)", children: _jsxs("form", { className: "content-grid", style: { gap: '1rem' }, onSubmit: handleCreateIncident, children: [_jsxs("div", { children: [_jsx("label", { children: "Name" }), _jsx("input", { value: createData.name, onChange: (event) => setCreateData((prev) => ({ ...prev, name: event.target.value })), required: true })] }), _jsxs("div", { children: [_jsx("label", { children: "Severity" }), _jsxs("select", { value: createData.severity, onChange: (event) => setCreateData((prev) => ({
                                        ...prev,
                                        severity: event.target.value
                                    })), children: [_jsx("option", { value: "maintenance", children: "Maintenance" }), _jsx("option", { value: "incident", children: "Incident" }), _jsx("option", { value: "critical", children: "Critical" })] })] }), _jsxs("div", { children: [_jsx("label", { children: "Description" }), _jsx("textarea", { rows: 4, value: createData.description, onChange: (event) => setCreateData((prev) => ({
                                        ...prev,
                                        description: event.target.value
                                    })), required: true })] }), createError && (_jsx("div", { style: { color: 'tomato' }, children: createError })), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx("button", { type: "submit", children: "Create incident" }) })] }) }), _jsx(Modal, { open: Boolean(statusModalIncident), title: "Change status", onClose: () => {
                    setStatusModalIncident(null);
                    setNewStatus('');
                    setStatusError(null);
                }, width: "min(420px, 90vw)", children: statusModalIncident ? (_jsxs("form", { className: "content-grid", style: { gap: '1rem' }, onSubmit: handleStatusSubmit, children: [_jsxs("div", { children: [_jsx("p", { style: { margin: 0 }, children: statusModalIncident.name }), _jsxs("small", { style: { color: 'rgba(255,255,255,0.7)' }, children: ["Current status: ", statusLabel[statusModalIncident.status]] })] }), availableStatusOptions.length ? (_jsxs("div", { children: [_jsx("label", { children: "Next status" }), _jsx("select", { value: newStatus, onChange: (event) => setNewStatus(event.target.value), required: true, children: availableStatusOptions.map((statusOption) => (_jsx("option", { value: statusOption, children: statusLabel[statusOption] }, statusOption))) })] })) : (_jsx("p", { children: "No further transitions available." })), statusError && (_jsx("div", { style: { color: 'tomato' }, children: statusError })), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx("button", { type: "submit", disabled: !availableStatusOptions.length, children: "Update status" }) })] })) : null }), _jsx(Modal, { open: Boolean(messageModalIncident), title: "Add message", onClose: () => {
                    setMessageModalIncident(null);
                    setMessageText('');
                    setMessageError(null);
                }, width: "min(520px, 95vw)", children: messageModalIncident ? (_jsxs("form", { className: "content-grid", style: { gap: '1rem' }, onSubmit: handleMessageSubmit, children: [_jsxs("div", { children: [_jsx("label", { children: "Message" }), _jsx("textarea", { rows: 4, value: messageText, onChange: (event) => setMessageText(event.target.value), required: true })] }), messageError && (_jsx("div", { style: { color: 'tomato' }, children: messageError })), _jsx("div", { style: { display: 'flex', justifyContent: 'flex-end' }, children: _jsx("button", { type: "submit", children: "Add message" }) })] })) : null }), _jsxs(Modal, { open: Boolean(deleteTarget), title: "Remove incident", onClose: () => setDeleteTarget(null), width: "min(420px, 90vw)", children: [_jsxs("p", { children: ["Remove incident", ' ', _jsx("strong", { children: deleteTarget ? deleteTarget.name : '' }), "? This cannot be undone."] }), _jsxs("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }, children: [_jsx("button", { type: "button", style: {
                                    background: 'transparent',
                                    border: '1px solid var(--color-border)',
                                    color: 'var(--color-text)'
                                }, onClick: () => setDeleteTarget(null), children: "Cancel" }), _jsx("button", { type: "button", style: { background: 'tomato', color: '#000' }, onClick: handleDeleteIncident, children: "Delete" })] })] })] }));
};
export default IncidentsPage;
